import React, { useState } from "react";
import {
  Button,
  FileUploadNew,
  FormFieldSet,
  Modal,
  showToast,
} from "../../../../components";
import { reportOptions } from "../../../../assets/data/fundingDashboardData";
import { usePagination } from "../../../../hooks";
import { DATE_RANGE_OPTIONS } from "../../../../assets/data/enumData";
import { getDatesByOption } from "../../../../utils/wf/date";
import { UploadModalTable } from "./UploadModalTable";
import axiosRequest from "../../../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../../../redux/apiConfig";
import { dateFormator, exactUTCDate } from "../../../../utils";
import dayjs from "dayjs";

const UploadModal = ({
  onClose,
  formData,
  handleChange,
  initialFormData,
  setFormData,
  rows,
  columns,
  loginUserUiid,
  handleDashboardCalls
}) => {
  const [uploadedFileModal, setUploadedFileModal] = useState(false);
  const [rowsForParsedData, setRowsForParsedData] = useState([]);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [isPreviewCustomRange, setIsPreviewCustomRange] = useState(false);
  const [resolvedRange, setResolvedRange] = useState({ from: null, to: null });
  const [previewResolvedRange, setPreviewResolvedRange] = useState({
    from: null,
    to: null,
  });

  const aging_duration_config = {
    "1_30": 1,
    "31_60": 2,
    "61_90": 3,
    "91_120": 4,
    "121_plus": 5,
  };
  const handleSubmit = async () => {
    try {
      setUploadedFileModal(false);
      const response = await axiosRequest({
        method: "POST",
        url: ENDPOINTS.WF_REPORTS.ADD,
        data: {
          coll: "Report",
          payload: {
            title: formData?.modalReportName,
            parsedExt: 1,
            period: {
                from: dayjs.utc(formData?.modalFromDate).toISOString(),
                to: dayjs.utc(formData?.modalToDate).toISOString()
            },
            report: rowsForParsedData?.map((inv) => ({
              client: inv?.client,
              invoices:
                inv?.invoices?.map((invoice) => ({
                  invoiceNum: invoice?.inv,
                  invoiceDate: invoice?.date,
                  batchNum: invoice?.batch,
                  age: invoice?.age,
                  amounts: {
                    invoiced: {
                      amt: invoice?.amount || 0,
                      duration: aging_duration_config[invoice?.bucket],
                    },
                    balance: invoice?.bal,
                  },
                })) || [],
              summary: {
                current: inv?.total?.current || 0,
                total: inv?.total?.bal,
                days1to30: inv?.total?.d1_30,
                days31to60: inv?.total?.d31_60,
                days61to90: inv?.total?.d61_90,
                days91to120: inv?.total?.d91_120,
                days121Plus: inv?.total?.d121,
              },
            })),
            addedBy: loginUserUiid,
          },
        },
      });
      console.log({ response });
      if (response.success) {
        showToast("Report uploaded successfully!", "success");
        setFormData({
          ...initialFormData,
          fromDate: formData?.fromDate,
          toDate: formData?.toDate,
          reportPeriod: formData?.reportPeriod,
          search: formData?.search,
        });
        onClose();
        handleDashboardCalls();
      } else {
        showToast(`${response.error || "Something went wrong!"}`, "error");
      }
    } catch (error) {
      console.error("Error in handleUpload in Aging Report", error);
      showToast(`${error?.message || "Something went wrong!"} `, "error");
    }
  };
  const handleCancel = () => {
    setFormData({
      ...initialFormData,
      fromDate: formData?.fromDate,
      toDate: formData?.toDate,
      reportPeriod: formData?.reportPeriod,
      search: formData?.search,
    });
    onClose();
  };

  const handleDateRangeSelect = (val) => {
    handleChange("modalDate", val);

    if (val === "custom") {
      setIsCustomRange(true);
      handleChange("modalFromDate", null);
      handleChange("modalToDate", null);
      setResolvedRange({ from: null, to: null });
    } else {
      setIsCustomRange(false);

      const range = getDatesByOption(val);

      if (range) {
        handleChange("modalFromDate", range?.from);
        handleChange("modalToDate", range?.to);
        setResolvedRange(range);
      }
    }
  };

  const handleFromChange = (val) => {
    handleChange("modalFromDate", val);
    if (val && formData?.modalToDate) {
      setResolvedRange({
        from: new Date(val),
        to: new Date(formData?.modalToDate),
      });
    }
  };

  const handleToChange = (val) => {
    handleChange("modalToDate", val);
    if (formData?.modalFromDate && val) {
      setResolvedRange({
        from: new Date(formData?.modalFromDate),
        to: new Date(val),
      });
    }
  };

  const handlePreviewDateRangeSelect = (val) => {
    handleChange("modalPreviewDate", val);
    if (val === "custom") {
      setIsPreviewCustomRange(true);
      handleChange("modalPreviewFromDate", null);
      handleChange("modalPreviewToDate", null);
      setPreviewResolvedRange({ from: null, to: null });
    } else {
      setIsPreviewCustomRange(false);
      handleChange("modalPreviewFromDate", null);
      handleChange("modalPreviewToDate", null);
      const range = getDatesByOption(val);
      if (range) setPreviewResolvedRange(range);
    }
  };

  const handlePreviewFromChange = (val) => {
    handleChange("modalPreviewFromDate", val);
    if (val && formData?.modalPreviewToDate) {
      setPreviewResolvedRange({
        from: new Date(val),
        to: new Date(formData?.modalPreviewToDate),
      });
    }
  };

  const handlePreviewToChange = (val) => {
    handleChange("modalPreviewToDate", val);
    if (formData?.modalPreviewFromDate && val) {
      setPreviewResolvedRange({
        from: new Date(formData?.modalPreviewFromDate),
        to: new Date(val),
      });
    }
  };

  const handleUpload = async () => {
    try {
      if (formData?.modalReportName !== "Aging Report") {
        throw new Error("Please select an Aging Report option");
      }
      if (!formData?.modalFile?.[0]) {
        throw new Error("Please select a file");
      }
      if(!formData?.modalFromDate || !formData?.modalToDate) {
        throw new Error("Please select a date range");
      }
      // throw new Error("Testing")
      const apiData = new FormData();
      apiData.append("coll", "Document");
      apiData.append("fileName", formData.modalReportName);
      apiData.append("doc", formData.modalFile[0]);
      apiData.append("payload", JSON.stringify({
        range: {
          from: dayjs.utc(formData?.modalFromDate).toISOString(),
          to: dayjs.utc(formData?.modalToDate).toISOString(),
        },
      }));
      const response = await axiosRequest({
        method: "POST",
        url: ENDPOINTS.FUNDING_DASHBOARD.FILE_PARSE,
        data: apiData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      // console.log({ response })
      if (response.success) {
        setUploadedFileModal(true);
        const agingdata =
          response?.data?.data?.map((row) => {
            const mappedInvoices = row?.invoices?.map((inv) => {
                let resolvedBucket = inv?.bucket;
                const ageNum = Number(inv?.age);
                if (!isNaN(ageNum)) {
                  if (ageNum >= 1 && ageNum <= 30) resolvedBucket = "1_30";
                  else if (ageNum >= 31 && ageNum <= 60) resolvedBucket = "31_60";
                  else if (ageNum >= 61 && ageNum <= 90) resolvedBucket = "61_90";
                  else if (ageNum >= 91 && ageNum <= 120) resolvedBucket = "91_120";
                  else if (ageNum >= 121) resolvedBucket = "121_plus";
                }
                return {
                  inv: inv?.invoiceNumber || "",
                  // date: dateFormator(inv?.invoiceDate) || "",
                  date: exactUTCDate(inv?.invoiceDate) || "",
                  paidDate: "",
                  batch: inv?.batchNumber || "",
                  amount: inv?.invoiceAmount || 0,
                  bal: inv?.invoiceAmount || 0,
                  age: inv?.age || "-",
                  d1_30: resolvedBucket === "1_30" ? inv?.invoiceAmount || 0 : 0,
                  d31_60: resolvedBucket === "31_60" ? inv?.invoiceAmount || 0 : 0,
                  d61_90: resolvedBucket === "61_90" ? inv?.invoiceAmount || 0 : 0,
                  d91_120: resolvedBucket === "91_120" ? inv?.invoiceAmount || 0 : 0,
                  d121: resolvedBucket === "121_plus" ? inv?.invoiceAmount || 0 : 0,
                  bucket: resolvedBucket,
                };
            }) || [];

            const dynamicTotal = { bal: 0, d1_30: 0, d31_60: 0, d61_90: 0, d91_120: 0, d121: 0, current: 0 };
            mappedInvoices.forEach((i) => {
              dynamicTotal.bal += (i.bal || 0);
              dynamicTotal.d1_30 += (i.d1_30 || 0);
              dynamicTotal.d31_60 += (i.d31_60 || 0);
              dynamicTotal.d61_90 += (i.d61_90 || 0);
              dynamicTotal.d91_120 += (i.d91_120 || 0);
              dynamicTotal.d121 += (i.d121 || 0);
            });
            dynamicTotal.current = row?.summary?.[`current`] || 0;

            return {
              client: row?.customer || "",
              invoices: mappedInvoices,
              total: dynamicTotal,
            };
          }) || [];
        setRowsForParsedData(agingdata);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Error in handleUpload in Aging Report", error);
      showToast(`${error?.message || "Something went wrong!"} `, "error");
    }
  };
  return (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-5">
        <FormFieldSet
          title="Report Name"
          type="select"
          placeholder="Select Report Name"
          options={reportOptions}
          onChange={(val) => handleChange("modalReportName", val)}
          value={formData?.modalReportName}
        />

        <FormFieldSet
          title="Date Range"
          type="select"
          placeholder="Select Date Range"
          options={DATE_RANGE_OPTIONS}
          onChange={handleDateRangeSelect}
          value={formData?.modalDate}
          // disabled={!formData.modalReportName || formData.modalReportName === "Aging Report"}
          disabled={!formData.modalReportName}
        />

        {formData?.modalDate && (
          <FormFieldSet
            title="From"
            type="datepicker"
            placeholder="select Date"
            onChange={handleFromChange}
            value={formData?.modalFromDate}
            showDropdown={false}
            disabled={!isCustomRange}
          />
        )}

        {formData?.modalDate && (
          <FormFieldSet
            title="To"
            type="datepicker"
            placeholder="select Date"
            onChange={handleToChange}
            value={formData?.modalToDate}
            showDropdown={false}
            minDate={
              formData?.modalFromDate
                ? new Date(formData?.modalFromDate)
                : undefined
            }
            disabled={!isCustomRange}
          />
        )}
      </div>

      <FileUploadNew
        title="Add Attachment"
        color="info"
        accept="all"
        maxFiles={5}
        onChange={(files) => handleChange("modalFile", files)}
        multiple
      />

      {/* ── Actions ── */}
      <div className="flex justify-end gap-3 mt-5">
        <Button title={"Cancel"} onClick={handleCancel} variant="outlined" />
        <Button title={"Upload"} onClick={handleUpload} />
      </div>

      <Modal
        isOpen={uploadedFileModal}
        onClose={() => setUploadedFileModal(false)}
        title="Aging Report Preview"
        width="max-w-7xl"
        titleColor={"primary"}
      >
        <div>
          {/* <TableAdv
            columns={columns}
            showSlNo
            rows={filteredRows}
            totalCount={filteredRows?.length}
            page={page}
            limit={limit}
            onPaginationChange={setPage}
            onLimitChange={handleLimitChange}
            toolbarLeft={{
              search: false,
              filter: false,
              download: false,
              add: [
                {
                  component: (
                    <div className="flex gap-3 items-end mb-5 justify-center">
                      <div className="w-50">
                        <FormFieldSet
                          title="Date Range"
                          type="select"
                          size="sm"
                          placeholder="Select Date Range"
                          options={DATE_RANGE_OPTIONS}
                          onChange={handlePreviewDateRangeSelect}
                          value={formData.modalDate}
                        />
                      </div>

                      {isCustomRange && (
                        <div className="w-40">
                          <FormFieldSet
                            title="From"
                            size="sm"
                            type="datepicker"
                            placeholder="select Date"
                            onChange={handlePreviewFromChange}
                            value={formData.modalFromDate}
                            showDropdown={false}
                          />
                        </div>
                      )}

                      {isCustomRange && (
                        <div className="w-40">
                          <FormFieldSet
                            title="To"
                            size="sm"
                            type="datepicker"
                            placeholder="select Date"
                            onChange={handlePreviewToChange}
                            value={formData.modalToDate}
                            minDate={
                              formData.modalFromDate
                                ? new Date(formData.modalFromDate)
                                : undefined
                            }
                            showDropdown={false}
                          />
                        </div>
                      )}
                    </div>
                  ),
                },
              ],
            }}
          /> */}
          <div className="flex gap-3 items-end justify-start">
            <div className="min-w-40">
              <FormFieldSet
                title="Date Range"
                type="select"
                size="sm"
                placeholder="Select Date Range"
                options={DATE_RANGE_OPTIONS}
                onChange={() => {}}
                value={formData?.modalDate}
                disabled
              />
            </div>

            {formData?.modalDate && (
              <div className="w-40">
                <FormFieldSet
                  title="From"
                  size="sm"
                  type="datepicker"
                  placeholder="select Date"
                  onChange={() => {}}
                  value={formData?.modalFromDate}
                  showDropdown={false}
                  disabled
                />
              </div>
            )}

            {formData?.modalDate && (
              <div className="w-40">
                <FormFieldSet
                  title="To"
                  size="sm"
                  type="datepicker"
                  placeholder="select Date"
                  onChange={() => {}}
                  value={formData?.modalToDate}
                  minDate={
                    formData?.modalFromDate
                      ? new Date(formData?.modalFromDate)
                      : undefined
                  }
                  showDropdown={false}
                  disabled
                />
              </div>
            )}
          </div>
          <UploadModalTable rows={rowsForParsedData} columns={columns} />
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <Button
            title={"Cancel"}
            onClick={() => setUploadedFileModal(false)}
            variant="outlined"
          />
          <Button title={"Submit"} onClick={handleSubmit} />
        </div>
      </Modal>
    </div>
  );
};
export default UploadModal;
